/* eslint-disable sonarjs/cognitive-complexity, complexity */
import dynamic from 'next/dynamic';
import Head from 'next/head';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StructuredData from '../../components/StructuredData';
import BreadCrumb from '../../components/find/BreadCrumb';
import FindFeed from '../../components/find/FindFeed';
import Footer from '../../components/find/Footer';
import Header from '../../components/find/Header';
import Pagination from '../../components/find/Pagination';
import Title from '../../components/find/Title';
import { FAVICON } from '../../config/constants/images';
import { HREF_LANG_URL_DOMAINS } from '../../config/links/links';
import {
  BRAND_WOWCHER,
  FIND_FEED_PAGE_SIZE,
  SITE_LIVINGSOCIAL,
  SITE_LIVINGSOCIAL_IE,
  SITE_WOWCHER,
  SITE_WOWCHERIE,
} from '../../config/setup/setup';
import { getShopUrl, pageView } from '../../helpers/find';
import { isLivingSocialBrand, isWowcherBrand } from '../../helpers/pages';
import { isSSR } from '../../helpers/ssr';
import UndecoratedLayout from '../../layouts/UndecoratedLayout';
import { getClientSideDeals } from '../../redux/actions/clientRender';
import { getNavigation } from '../../redux/actions/navigation';
import { getServerSideDeals } from '../../redux/actions/ssr';
import { wrapper } from '../../redux/store/store';

const HeaderFooterLayout = dynamic(
  () => import('../../layouts/HeaderFooterLayout'),
  {
    ssr: true,
  },
);

const NavigationLinks = dynamic(
  () => import('../../components/find/NavigationLinks'),
  {
    ssr: true,
  },
);

const AdditionalContent = dynamic(
  () => import('../../components/find/AdditionalContent'),
  {
    ssr: true,
  },
);

const Inspirations = dynamic(
  () => import('../../components/find/Inspirations'),
  {
    ssr: true,
  },
);

const Recommendations = dynamic(
  () => import('../../components/find/Recommendations'),
  {
    ssr: true,
  },
);

const ViewMore = dynamic(() => import('../../components/find/ViewMore'), {
  ssr: true,
});

const ErrorPage = dynamic(() => import('../../components/errors/Form404'), {
  ssr: true,
});

const Subsubcat = ({ bt, isMobile = false, pageNumber, path, query, url }) => {
  const dispatch = useDispatch();
  const isSpaPage = url.includes('/beauty/spa');

  const [deals, facetedNavigation, ssr] = useSelector((state) => [
    state.deals.deals,
    state.deals.facetedNavigation,
    state.deals.ssr,
  ]);
  const count = facetedNavigation.count;
  const pageSize = FIND_FEED_PAGE_SIZE;
  const lastPage = Math.ceil(count / pageSize);
  const title = facetedNavigation.pageTitle;
  const text = facetedNavigation.text;
  const isWowcher = isWowcherBrand();
  const isLivingSocial = isLivingSocialBrand();
  const Environment = process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER;
  const fav = FAVICON[Environment];
  const urlForHref = url.split('?')[0];
  const isHrefCompatible =
    urlForHref.includes('shop') || urlForHref.includes('travel');
  const currentPage =
    pageNumber > 0 ? (pageNumber === 1 ? null : pageNumber) : null;

  useEffect(() => {
    async function fetchClientSideDeals() {
      await dispatch(
        getClientSideDeals({
          emailDealLocation: null,
          isEmailDeal: false,
          isPreview: false,
          pageNumber,
          pageSize: FIND_FEED_PAGE_SIZE,
          path,
          query,
          secondaryDealsCount: 0,
          sideDealsLocal: false,
        }),
      );
    }
    fetchClientSideDeals();
    pageView({
      category: facetedNavigation?.breadcrumbs?.[1]?.linkText,
      subcategory: facetedNavigation?.breadcrumbs?.[2]?.linkText,
    });
  }, []);

  if (deals.length === 0) {
    return (
      <>
        <Head>
          <title> Page not found</title>
          {deals.length && <StructuredData deals={deals} isMobile={isMobile} />}
        </Head>
        <Header bt={bt} isMobile={isMobile} />
        <ErrorPage statusCode={404} />
        <Footer />
      </>
    );
  }

  if (Environment !== BRAND_WOWCHER) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      <Head>
        <title>
          {facetedNavigation?.metaTitle
            ? `${facetedNavigation.metaTitle} ${
                currentPage ? ` - Page ${currentPage}` : ''
              }`
            : `${title} - Wowcher${
                currentPage ? ` - Page ${currentPage}` : ''
              }`}
        </title>
        <link href={fav} rel="icon" />
        <meta
          content={facetedNavigation?.metaTitle || `${title} - Wowcher`}
          name="title"
        />
        <meta content={facetedNavigation?.metaDescription} name="description" />
      </Head>
      {isWowcher && isHrefCompatible && (
        <Head>
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_WOWCHER]
            }${urlForHref}`}
            hrefLang="en-GB"
            rel="alternate"
          />
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_WOWCHERIE]
            }${urlForHref}`}
            hrefLang="en-IE"
            rel="alternate"
          />
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_WOWCHER]
            }${urlForHref}`}
            hrefLang="x-default"
            rel="alternate"
          />
        </Head>
      )}
      {isLivingSocial && isHrefCompatible && (
        <Head>
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_LIVINGSOCIAL]
            }${urlForHref}`}
            hrefLang="en-GB"
            rel="alternate"
          />
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_LIVINGSOCIAL_IE]
            }${urlForHref}`}
            hrefLang="en-IE"
            rel="alternate"
          />
          <link
            href={`${
              HREF_LANG_URL_DOMAINS[
                process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'
              ][SITE_LIVINGSOCIAL]
            }${urlForHref}`}
            hrefLang="x-default"
            rel="alternate"
          />
        </Head>
      )}
      <StructuredData deals={deals} isMobile={isMobile} />
      <Header bt={bt} isMobile={isMobile} path={path} />
      <BreadCrumb breadcrumbs={facetedNavigation.breadcrumbs} />
      <Title count={count} text={text} title={title} />
      {facetedNavigation.navigations.length > 0 && (
        <NavigationLinks
          isMobile={isMobile}
          navigations={facetedNavigation?.navigations}
          url={url}
        />
      )}
      {
        <NavigationLinks
          isMobile={isMobile}
          navigations={facetedNavigation.recommendations?.slice(0, 4)}
          recommendation
          url={url}
        />
      }

      <FindFeed
        deals={deals}
        isMobile={isMobile}
        isSpaPage={isSpaPage}
        ssr={ssr}
      />
      {lastPage > 1 && (
        <Pagination lastPage={lastPage} pageNumber={pageNumber} url={url} />
      )}
      {facetedNavigation?.navigations?.length > 0 && (
        <ViewMore
          navigations={facetedNavigation?.navigations}
          title={facetedNavigation?.breadcrumbs?.[2]?.linkText}
        />
      )}
      {facetedNavigation?.recommendations?.length > 0 && (
        <Recommendations
          recommendations={facetedNavigation.recommendations}
          title={facetedNavigation.pageTitle}
        />
      )}
      {facetedNavigation?.additionalContent && (
        <AdditionalContent content={facetedNavigation?.additionalContent} />
      )}
      {facetedNavigation?.inspirations?.length > 0 && (
        <Inspirations inspirations={facetedNavigation.inspirations} />
      )}
      <Footer site={process.env.NEXT_PUBLIC_SITE} />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ store, req, query }) => {
    const cleanUrl = req?.url.includes('/_next/data')
      ? req?.url.replace(/_next\/data\/.+?\/|\.json/g, '')
      : req?.url;
    if (!cleanUrl.includes('/shop') && !cleanUrl.includes('/beauty/spa')) {
      return {
        redirect: {
          destination: getShopUrl(cleanUrl.split('?')[0]),
          statusCode: 301,
        },
      };
    }
    const userAgent = req.headers['user-agent'];

    const pathName = req.url.replace('find', 'deals');

    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent,
    );

    await store
      .dispatch(
        getServerSideDeals({
          pageNumber: query?.page || null,
          pageSize: FIND_FEED_PAGE_SIZE,
          path: pathName,
          query,
          ssr: true,
        }),
      )
      .catch((error) => {
        console.error(`getServerSideDeals error: ${error}`);
      });
    await store.dispatch(getNavigation('london', isSSR())).catch((error) => {
      console.error(`getNavigation error: ${error}`);
    });

    if (store.getState().deals.deals.length === 0 && !query?.page) {
      const parentUrl = cleanUrl
        .slice(0, Math.max(0, cleanUrl.lastIndexOf('/')))
        .replace('find', 'deals');

      return {
        redirect: {
          destination: parentUrl,
          statusCode: 302,
        },
      };
    }

    if (query?.page === '1') {
      return {
        redirect: {
          destination: cleanUrl.split('?')[0],
          statusCode: 302,
        },
      };
    }

    return {
      props: {
        bt: req?.cookies?.bt || null,
        isMobile,
        pageNumber: query?.page || null,
        path: pathName,
        query,
        url: cleanUrl,
        userAgent,
      },
    };
  },
);

export default Subsubcat;

Subsubcat.layout =
  process.env.NEXT_PUBLIC_BRAND === BRAND_WOWCHER
    ? UndecoratedLayout
    : HeaderFooterLayout;

Subsubcat.propTypes = {
  bt: PropTypes.string,
  isMobile: PropTypes.bool,
  pageNumber: PropTypes.string,
  path: PropTypes.string,
  query: PropTypes.object,
  url: PropTypes.string,
};
